import { create } from 'zustand';
import type { CriarEscritorioDto, Eu, Papel } from '@social/shared';
import { criarCliente, ErroApi, type Cliente } from './api';

const CHAVE_REFRESH = 'social.refresh';

interface Sessao {
  acesso: string;
  refresh: string;
}

interface EstadoSessao {
  /**
   * O access vive SÓ EM MEMÓRIA.
   *
   * Guardá-lo no `localStorage` daria a um XSS uma credencial usável até
   * expirar, de qualquer aba, mesmo depois de a página fechar. Em memória, o
   * estrago acaba quando a aba fecha.
   *
   * O refresh vai para o `localStorage` porque precisa sobreviver ao reload —
   * e ele é ROTACIONADO a cada uso, o que torna o roubo detectável: quando o
   * ladrão usa, o dono é deslogado.
   */
  acesso: string | null;
  papel: Papel | null;
  restaurando: boolean;
  /**
   * Nome, e-mail e escritório de quem está logado. Vem de `GET /api/eu`
   * (Task 1) — não do token, que só carrega o suficiente para AUTORIZAR.
   *
   * Chega DEPOIS do `acesso`, numa segunda chamada, e pode nunca chegar (ver
   * `buscarEu`): a casca não pode travar o login por causa disso, então todo
   * lugar que exibe `eu` precisa saber cair para `ROTULO_PAPEL[papel]`.
   */
  eu: Eu | null;
  entrar: (email: string, senha: string) => Promise<void>;
  /**
   * Autocadastro: cria o escritório e JÁ ENTRA com ele.
   *
   * Vive na store, e não solto na tela, porque o efeito é o mesmo do `entrar`
   * — token guardado, papel lido, `eu` carregado. Fora daqui, a tela teria de
   * repetir esses quatro passos e um deles seria esquecido.
   */
  criarEscritorio: (dados: CriarEscritorioDto) => Promise<void>;
  /**
   * Relê o `eu` sem mexer no token.
   *
   * A casca mostra o nome no canto: quem o edita na tela de conta veria o
   * nome VELHO até o próximo login se a sessão não fosse relida.
   */
  recarregarEu: () => Promise<void>;
  /**
   * Adota uma sessão emitida por outro caminho que não o login.
   *
   * Existe para a troca de senha: ela derruba TODOS os refresh do usuário, o
   * desta aba incluído, e o servidor devolve um par novo. Sem adotá-lo, quem
   * acabou de trocar a própria senha seria expulso na renovação seguinte.
   */
  adotarSessao: (s: Sessao) => Promise<void>;
  sair: () => Promise<void>;
  restaurar: () => Promise<void>;
}

interface Conteudo {
  tenantId: string;
  usuarioId: string;
  papel: Papel;
}

/**
 * Lê o papel do payload do JWT SEM verificar a assinatura.
 *
 * Quem verifica é a API, a cada requisição. Aqui o papel serve só para a tela
 * decidir o que mostrar — se alguém adulterar o token no navegador, ganha
 * botões que o servidor recusa.
 */
function lerPapel(acesso: string): Papel | null {
  try {
    const parte = acesso.split('.')[1];
    if (!parte) return null;
    const json = atob(parte.replace(/-/g, '+').replace(/_/g, '/'));
    return (JSON.parse(json) as Conteudo).papel;
  } catch {
    return null;
  }
}

/**
 * Renovação com deduplicação de chamada EM VOO.
 *
 * O refresh é rotacionado a cada uso: quem chega segundo com o mesmo token
 * recebe 401. Duas renovações concorrentes, então, não são "uma redundante" —
 * a segunda DESLOGA quem já tinha entrado, porque o `catch` do `restaurar`
 * apaga a sessão.
 *
 * E concorrência aqui é o caso comum, não o raro:
 *  - o `StrictMode` monta o efeito duas vezes em desenvolvimento, então a
 *    sessão nunca sobrevivia a um reload local;
 *  - duas requisições que expiram juntas chamam `aoRenovar` juntas;
 *  - `restaurar` e um `aoRenovar` podem se cruzar na abertura.
 *
 * Todas passam a esperar a MESMA promessa. A porta fecha quando ela assenta,
 * para que a próxima renovação legítima use o token já rotacionado.
 *
 * Fora do alcance disto: duas ABAS abrindo ao mesmo tempo têm cada uma seu
 * módulo, disputam o mesmo refresh no `localStorage`, e uma delas cai. Isso
 * exigiria trava entre abas (Web Locks) — ver CLAUDE_CONTEXTO.
 */
let renovacaoEmVoo: Promise<Sessao> | null = null;

function renovar(refresh: string): Promise<Sessao> {
  renovacaoEmVoo ??= trocar('/auth/renovar', { refresh })
    .then((s) => {
      localStorage.setItem(CHAVE_REFRESH, s.refresh);
      return s;
    })
    .finally(() => {
      renovacaoEmVoo = null;
    });
  return renovacaoEmVoo;
}

/**
 * Guarda a sessão recebida e carrega o `eu`.
 *
 * Um lugar só, usado pelo login e pelo autocadastro: eram os mesmos quatro
 * passos, e o segundo caminho copiado seria o que esqueceria a testemunha
 * abaixo.
 */
async function adotar(
  s: Sessao,
  set: (parcial: Partial<EstadoSessao>) => void,
  get: () => EstadoSessao,
): Promise<void> {
  localStorage.setItem(CHAVE_REFRESH, s.refresh);
  set({ acesso: s.acesso, papel: lerPapel(s.acesso) });
  const eu = await buscarEu();
  // `acesso` é a testemunha: se mudou enquanto `buscarEu` estava em voo
  // (ex.: `sair()` rodou no meio do caminho), esta resposta é de uma sessão
  // que não existe mais — aplicá-la ressuscitaria um `eu` de quem já saiu.
  if (get().acesso === s.acesso) set({ eu });
}

async function trocar(caminho: string, dados: unknown): Promise<Sessao> {
  const resposta = await fetch(`/api${caminho}`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(dados),
  });
  if (!resposta.ok) {
    const e = (await resposta.json().catch(() => ({}))) as {
      erro?: { codigo?: string; mensagem?: string };
    };
    throw new ErroApi(
      e.erro?.codigo ?? 'credenciais_invalidas',
      e.erro?.mensagem ?? 'E-mail ou senha incorretos.',
      resposta.status,
    );
  }
  return (await resposta.json()) as Sessao;
}

export const useSessao = create<EstadoSessao>((set, get) => ({
  acesso: null,
  papel: null,
  restaurando: true,
  eu: null,

  entrar: async (email, senha) => {
    await adotar(await trocar('/auth/entrar', { email, senha }), set, get);
  },

  criarEscritorio: async (dados) => {
    await adotar(await trocar('/auth/escritorios', dados), set, get);
  },

  adotarSessao: async (nova) => {
    await adotar(nova, set, get);
  },

  recarregarEu: async () => {
    const eu = await buscarEu();
    // Só aplica se ainda há sessão: sem esta guarda, um `sair()` no meio do
    // caminho seria desfeito por uma resposta em voo.
    if (get().acesso) set({ eu });
  },

  sair: async () => {
    const refresh = localStorage.getItem(CHAVE_REFRESH);
    localStorage.removeItem(CHAVE_REFRESH);
    set({ acesso: null, papel: null, eu: null });
    if (refresh) await trocar('/auth/sair', { refresh }).catch(() => undefined);
  },

  restaurar: async () => {
    const refresh = localStorage.getItem(CHAVE_REFRESH);
    if (!refresh) {
      set({ restaurando: false });
      return;
    }
    try {
      const s = await renovar(refresh);
      set({ acesso: s.acesso, papel: lerPapel(s.acesso), restaurando: false });
      const eu = await buscarEu();
      // Mesma guarda de `entrar`: sem ela, um `sair()` no meio da restauração
      // reapareceria com o `eu` de quem acabou de ser deslogado.
      if (get().acesso === s.acesso) set({ eu });
    } catch {
      localStorage.removeItem(CHAVE_REFRESH);
      set({ acesso: null, papel: null, restaurando: false });
    }
  },
}));

/**
 * Busca `GET /api/eu` depois de o acesso já existir na sessão.
 *
 * Nunca lança: autenticar e enfeitar são coisas diferentes. Se isto falhar
 * (rede, API fora, o que for), o usuário já entrou — a casca só cai para
 * `ROTULO_PAPEL[papel]`, que já veio do token.
 */
async function buscarEu(): Promise<Eu | null> {
  try {
    return await api.get<Eu>('/eu');
  } catch {
    return null;
  }
}

export const api: Cliente = criarCliente({
  buscar: (...args) => fetch(...args),
  obterAcesso: () => useSessao.getState().acesso,
  aoRenovar: async () => {
    const refresh = localStorage.getItem(CHAVE_REFRESH);
    if (!refresh) throw new ErroApi('sessao_invalida', 'Entre de novo.', 401);
    const s = await renovar(refresh);
    useSessao.setState({ acesso: s.acesso, papel: lerPapel(s.acesso) });
    return s.acesso;
  },
});
